import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Table2,
  FileText,
  Filter,
  Heart,
  MessageCircle,
  Copy,
  ChevronRight,
  Clock,
  User,
  TrendingUp,
  Users,
  Building2,
  Flame,
} from "lucide-react";
import { useTeamStore, DEPARTMENTS } from "../../../stores/teamStore";
import { teamService } from "../../../services/teamService";
import { formatDate } from "../../../utils/dateGrouping";

// Department Tab Component
const DepartmentTabs = ({ activeDepartment, onDepartmentChange }) => {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
      {DEPARTMENTS.map((dept) => {
        const isActive = activeDepartment === dept.id;
        return (
          <button
            key={dept.id}
            onClick={() => onDepartmentChange(dept.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              isActive
                ? "bg-sedaily-500 text-white"
                : "bg-bg-200/50 text-text-400 hover:bg-bg-300/50 hover:text-text-200"
            }`}
          >
            {dept.id === 'all' && <Users className="w-3.5 h-3.5" />}
            {dept.label}
          </button>
        );
      })}
    </div>
  );
};

// Sort Options Component
const SortOptions = ({ sortBy, onSortChange }) => {
  const options = [
    { id: 'recent', label: 'Newest', icon: Clock },
    { id: 'popular', label: 'Popular', icon: Flame },
    { id: 'myDepartment', label: 'My Team', icon: Building2 },
  ];

  return (
    <div className="flex items-center gap-1 bg-bg-200/30 rounded-lg p-0.5">
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = sortBy === option.id;
        return (
          <button
            key={option.id}
            onClick={() => onSortChange(option.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
              isActive
                ? "bg-bg-100 text-text-100 shadow-sm"
                : "text-text-400 hover:text-text-200"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

// Type Filter Tab Component
const TypeFilterTabs = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { id: "all", label: "All", icon: null },
    { id: "table", label: "Table", icon: Table2 },
    { id: "document", label: "Document", icon: FileText },
    { id: "search", label: "Search", icon: Search },
  ];

  return (
    <div className="flex items-center gap-0.5">
      {filters.map((filter) => {
        const Icon = filter.icon;
        const isActive = activeFilter === filter.id;
        return (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              isActive
                ? "bg-bg-200 text-text-100"
                : "text-text-400 hover:text-text-200 hover:bg-bg-200/50"
            }`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};

// Department Badge Component
const DepartmentBadge = ({ department, isMyDepartment }) => {
  const getDepartmentColor = (dept) => {
    const colors = {
      'IT': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'Industry': 'bg-green-500/10 text-green-400 border-green-500/20',
      'Economy': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      'Society': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Politics': 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return colors[dept] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border ${getDepartmentColor(department)}`}>
      <Building2 className="w-3 h-3" />
      {department}
      {isMyDepartment && <span className="text-[10px] opacity-70">(My Team)</span>}
    </span>
  );
};

// Work Card Component
const WorkCard = ({ work, onClick, currentUserDepartment }) => {
  const [isLiked, setIsLiked] = useState(work.likedBy?.includes("user_current"));
  const [likeCount, setLikeCount] = useState(work.likes || 0);
  const { toggleLike } = useTeamStore();
  const isMyDepartment = work.department === currentUserDepartment;

  const handleLike = (e) => {
    e.stopPropagation();
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : prev - 1);
    toggleLike(work.id, "user_current");
  };

  const getWorkTypeIcon = () => {
    switch (work.workType) {
      case "table":
        return <Table2 className="w-3.5 h-3.5" />;
      case "document":
        return <FileText className="w-3.5 h-3.5" />;
      case "search":
        return <Search className="w-3.5 h-3.5" />;
      default:
        return <FileText className="w-3.5 h-3.5" />;
    }
  };

  const getWorkTypeLabel = () => {
    switch (work.workType) {
      case "table":
        return "Table";
      case "document":
        return "Document";
      case "search":
        return "Search";
      default:
        return "Other";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      onClick={() => onClick(work.id)}
      className={`bg-bg-200/60 rounded-2xl overflow-hidden cursor-pointer transition-all group ${
        isMyDepartment ? 'ring-1 ring-sedaily-500/30' : ''
      }`}
      style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
    >
      {/* Preview Area */}
      <div className="h-28 bg-bg-300/30 p-3 overflow-hidden relative">
        {work.table ? (
          <div className="text-xs text-text-400 font-mono overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  {work.table.headers?.slice(0, 3).map((h, i) => (
                    <th key={i} className="text-left pr-2 text-text-300 text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {work.table.rows?.slice(0, 2).map((row, i) => (
                  <tr key={i}>
                    {row.slice(0, 3).map((cell, j) => (
                      <td key={j} className="pr-2 truncate max-w-[80px] text-[10px]">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs text-text-400 line-clamp-4">
            {work.content}
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-bg-300/30 to-transparent" />
      </div>

      {/* Content Area */}
      <div className="p-4">
        {/* Badges Row */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <DepartmentBadge department={work.department} isMyDepartment={isMyDepartment} />
          <span className="flex items-center gap-1 px-2 py-0.5 bg-bg-300/50 text-text-400 text-xs rounded-md">
            {getWorkTypeIcon()}
            {getWorkTypeLabel()}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-text-100 mb-1.5 group-hover:text-sedaily-400 transition-colors line-clamp-1">
          {work.title}
        </h3>

        {/* Description */}
        {work.description && (
          <p className="text-xs text-text-400 mb-2.5 line-clamp-2">{work.description}</p>
        )}

        {/* Tags */}
        {work.tags && work.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {work.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-[10px] text-text-500 bg-bg-300/30 px-1.5 py-0.5 rounded"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Author & Stats */}
        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid hsl(var(--border-300) / 0.08)' }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-sedaily-500/80 flex items-center justify-center">
              <User className="w-2.5 h-2.5 text-white" />
            </div>
            <span className="text-xs text-text-300">{work.userName}</span>
            <span className="text-[10px] text-text-500">
              {formatDate(work.createdAt)}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 text-xs transition-colors ${
                isLiked ? "text-red-400" : "text-text-400 hover:text-red-400"
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${isLiked ? "fill-current" : ""}`} />
              {likeCount}
            </button>
            <span className="flex items-center gap-1 text-xs text-text-400">
              <MessageCircle className="w-3.5 h-3.5" />
              {work.commentCount || 0}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Team Library Component
const TeamLibrary = () => {
  const navigate = useNavigate();
  const {
    works,
    setWorks,
    filter,
    setFilter,
    department,
    setDepartment,
    sortBy,
    setSortBy,
    currentUser,
    getFilteredWorks
  } = useTeamStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Load works on mount
  useEffect(() => {
    const loadWorks = async () => {
      setIsLoading(true);
      try {
        const data = await teamService.getWorks();
        setWorks(data);
      } catch (error) {
        console.error("Failed to load works:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadWorks();
  }, [setWorks]);

  // Get filtered works from store and apply search
  const filteredWorks = getFilteredWorks().filter((work) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      work.title.toLowerCase().includes(query) ||
      work.description?.toLowerCase().includes(query) ||
      work.userName?.toLowerCase().includes(query) ||
      work.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  const handleWorkClick = (workId) => {
    navigate(`/team/${workId}`);
  };

  // Stats for header
  const totalWorks = works.length;
  const myDepartmentWorks = works.filter(w => w.department === currentUser.department).length;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-text-100">Team Library</h1>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-text-400">
                Total <span className="font-semibold text-text-200">{totalWorks}</span>
              </span>
              <span className="text-text-400">
                My Team <span className="font-semibold text-sedaily-400">{myDepartmentWorks}</span>
              </span>
            </div>
          </div>
          <p className="text-text-400 text-sm">Browse and use results shared by your team</p>
        </div>

        {/* Department Tabs */}
        <div className="mb-4">
          <DepartmentTabs activeDepartment={department} onDepartmentChange={setDepartment} />
        </div>

        {/* Filters, Sort & Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid hsl(var(--border-300) / 0.08)' }}>
          <div className="flex items-center gap-3">
            <TypeFilterTabs activeFilter={filter} onFilterChange={setFilter} />
            <div className="w-px h-5 bg-border-300/20" />
            <SortOptions sortBy={sortBy} onSortChange={setSortBy} />
          </div>

          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-400" />
            <input
              type="text"
              placeholder="Search by title, author, tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-bg-200/30 text-text-100 text-sm pl-9 pr-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-colors placeholder:text-text-500"
              style={{ border: '1px solid hsl(var(--border-300) / 0.08)' }}
            />
          </div>
        </div>

        {/* Works Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sedaily-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredWorks.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-bg-200/50 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-text-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-200 mb-2">
              {searchQuery ? "No search results" : "No shared results yet"}
            </h3>
            <p className="text-text-400 text-sm">
              {searchQuery
                ? "Try a different search term"
                : "Share results from a conversation with your team"}
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs text-text-500 mb-3">
              {filteredWorks.length} result(s)
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <AnimatePresence>
                {filteredWorks.map((work) => (
                  <WorkCard
                    key={work.id}
                    work={work}
                    onClick={handleWorkClick}
                    currentUserDepartment={currentUser.department}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamLibrary;
